import { useEffect, useRef, useState } from 'react';
import { useUiStore } from '@/store/uiStore';

const STORAGE_KEY = 'cseview-mcp-onboarding-closed';
// Fires well after first paint so this one-time modal's media never competes
// for LCP (see useNudgeTimer for the same modal-stacking guard pattern).
const REVEAL_DELAY_MS = 4000;

export function useMcpOnboarding() {
  const [isOpen, setIsOpen] = useState(false);
  const openModalCount = useUiStore((s) => s.openModalCount);
  const queuedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(STORAGE_KEY)) return;

    let timeoutId: number | undefined;
    function arm() {
      timeoutId = window.setTimeout(() => {
        if (useUiStore.getState().openModalCount > 0) {
          queuedRef.current = true;
        } else {
          setIsOpen(true);
        }
      }, REVEAL_DELAY_MS);
    }

    if (document.readyState === 'complete') {
      arm();
    } else {
      window.addEventListener('load', arm, { once: true });
    }

    return () => {
      window.removeEventListener('load', arm);
      window.clearTimeout(timeoutId);
    };
  }, []);

  // Release effect: reveal a queued onboarding modal once other modals clear.
  useEffect(() => {
    if (queuedRef.current && openModalCount === 0) {
      queuedRef.current = false;
      setIsOpen(true);
    }
  }, [openModalCount]);

  function close() {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, '1');
    }
    setIsOpen(false);
  }

  return { isOpen, close };
}
